/**
 * DrugReferencesPanel
 * =============================================================================
 * Renders the "Drugs referenced in this letter" cross-reference panel inside
 * the FDA-letter view on ``DrugDetailPage`` (and reusable elsewhere).
 *
 * Backed by ``drug_references`` from ``GET /api/fda-letters/documents/{id}``
 * (PR #51): a list of drugs the letter links to via the ``fda_letter_drugs``
 * M2M, already sorted target → comparator → mentioned with doc order within
 * each role.
 *
 * Render contract:
 *   - If refs is empty or undefined → render nothing (avoids visual noise on
 *     inspection / IRB / internet-pharmacy letters that have no drug refs).
 *   - Otherwise, one row per ref with a role badge, brand name, application
 *     number, manufacturer, and a truncated context snippet.  Each row links
 *     to ``/drug/{ref.drug_id}?source=fda-letter&version={letterId}`` so
 *     letter-mode persists across navigation to a different drug, and the
 *     self-row stays on the current URL (true no-op).
 *
 * Item [3] of docs/superpowers/plans/2026-05-14-fda-letters-post-search-spec.md.
 * Worst case the in-flight FdaLetterDetailPage refactor extracts a shared
 * ``<FdaLetterView>`` and dedups — small future refactor, not a blocker now.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import type { FdaLetterDrugReference } from '../../types/fdaLetter';

interface Props {
  references: FdaLetterDrugReference[] | undefined;
  /** Letter id used to preserve ?source=fda-letter&version={letterId} on the
   *  per-row link.  Without this, clicking the self-row navigates out of the
   *  letter view and clicking other rows loses letter-mode entirely. */
  letterId: number;
}

const SNIPPET_MAX_LENGTH = 200;

const truncateSnippet = (snippet: string | null): string | null => {
  if (!snippet) return null;
  if (snippet.length <= SNIPPET_MAX_LENGTH) return snippet;
  return snippet.slice(0, SNIPPET_MAX_LENGTH).trimEnd() + '…';
};

const roleLabel = (role: FdaLetterDrugReference['role']): string => {
  if (role === 'target') return 'Target';
  if (role === 'comparator') return 'Comparator';
  return 'Mentioned';
};

const roleBadgeClasses = (role: FdaLetterDrugReference['role']): string => {
  // Match the color palette used elsewhere in the page for status pills:
  // amber for FDA-letter context, neutral slate for less-prominent roles.
  if (role === 'target') {
    return 'bg-amber-100 text-amber-800 border-amber-300';
  }
  if (role === 'comparator') {
    return 'bg-indigo-50 text-indigo-700 border-indigo-200';
  }
  return 'bg-slate-50 text-slate-600 border-slate-200';
};

const DrugReferencesPanel: React.FC<Props> = ({ references, letterId }) => {
  if (!references || references.length === 0) {
    return null;
  }

  return (
    <div className="p-6 border-b border-slate-200">
      <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
        Drugs referenced in this letter
      </h3>
      <ul className="space-y-2">
        {references.map((ref, idx) => {
          const snippet = truncateSnippet(ref.context_snippet);
          const displayName =
            ref.brand_name || ref.generic_name || `Drug #${ref.drug_id}`;
          return (
            <li
              key={`${ref.drug_id}-${ref.role}-${idx}`}
              className="border border-slate-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50/40 transition-colors"
            >
              <Link
                to={`/drug/${ref.drug_id}?source=fda-letter&version=${letterId}`}
                className="block p-3 group"
              >
                <div className="flex items-start gap-2 flex-wrap">
                  <span
                    className={`inline-block px-2 py-0.5 text-xs font-semibold uppercase rounded border ${roleBadgeClasses(
                      ref.role,
                    )}`}
                  >
                    {roleLabel(ref.role)}
                  </span>
                  <span className="font-semibold text-slate-900 group-hover:text-indigo-700">
                    {displayName}
                  </span>
                  {ref.application_number && (
                    <span className="text-xs text-slate-500">
                      {ref.application_number}
                    </span>
                  )}
                  {ref.manufacturer && (
                    <span className="text-xs text-slate-500">
                      · {ref.manufacturer}
                    </span>
                  )}
                </div>
                {snippet && (
                  <blockquote className="mt-2 pl-3 border-l-2 border-slate-200 text-sm italic text-slate-600">
                    {snippet}
                  </blockquote>
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default DrugReferencesPanel;
